// DataHub RBAC Utilities
// Permission checking and validation functions

import { getRolePermissions } from '../constants/roles';

// ==============================|| PERMISSION CHECKING ||============================== //

/**
 * Check if user has specific permission
 * @param {Array} userPermissions - Array of user permissions
 * @param {string} resource - Resource name (e.g., 'projects', 'files')
 * @param {string} action - Action name (e.g., 'create', 'read', 'update')
 * @returns {boolean} - True if user has permission
 */
export const hasPermission = (userPermissions, resource, action) => {
    if (!userPermissions || !Array.isArray(userPermissions)) {
        return false;
    }

    const requiredPermission = `${resource}:${action}`;
    const wildcardPermission = `${resource}:*`;
    const superPermission = '*';

    return (
        userPermissions.includes(requiredPermission) ||
        userPermissions.includes(wildcardPermission) ||
        userPermissions.includes(superPermission)
    );
};

/**
 * Check if user has any of the specified permissions
 * @param {Array} userPermissions - Array of user permissions
 * @param {Array} requiredPermissions - Array of required permissions
 * @returns {boolean} - True if user has any of the required permissions
 */
export const hasAnyPermission = (userPermissions, requiredPermissions) => {
    if (!userPermissions || !Array.isArray(userPermissions)) {
        return false;
    }

    return requiredPermissions.some((permission) => {
        if (typeof permission === 'string') {
            return hasPermission(userPermissions, ...permission.split(':'));
        }
        return false;
    });
};

/**
 * Check if user has all of the specified permissions
 * @param {Array} userPermissions - Array of user permissions
 * @param {Array} requiredPermissions - Array of required permissions
 * @returns {boolean} - True if user has all required permissions
 */
export const hasAllPermissions = (userPermissions, requiredPermissions) => {
    if (!userPermissions || !Array.isArray(userPermissions)) {
        return false;
    }

    return requiredPermissions.every((permission) => {
        if (typeof permission === 'string') {
            return hasPermission(userPermissions, ...permission.split(':'));
        }
        return false;
    });
};

// ==============================|| ROLE CHECKING ||============================== //

/**
 * Check if user has specific role
 * @param {Array} userRoles - Array of user roles
 * @param {string} roleName - Role name to check
 * @returns {boolean} - True if user has the role
 */
export const hasRole = (userRoles, roleName) => {
    if (!userRoles || !Array.isArray(userRoles)) {
        return false;
    }

    return userRoles.includes(roleName);
};

/**
 * Check if user has any of the specified roles
 * @param {Array} userRoles - Array of user roles
 * @param {Array} requiredRoles - Array of required roles
 * @returns {boolean} - True if user has any of the required roles
 */
export const hasAnyRole = (userRoles, requiredRoles) => {
    if (!userRoles || !Array.isArray(userRoles)) {
        return false;
    }

    return requiredRoles.some((role) => userRoles.includes(role));
};

/**
 * Check if user has all of the specified roles
 * @param {Array} userRoles - Array of user roles
 * @param {Array} requiredRoles - Array of required roles
 * @returns {boolean} - True if user has all required roles
 */
export const hasAllRoles = (userRoles, requiredRoles) => {
    if (!userRoles || !Array.isArray(userRoles)) {
        return false;
    }

    return requiredRoles.every((role) => userRoles.includes(role));
};

// ==============================|| RESOURCE ACCESS VALIDATION ||============================== //

/**
 * Check if user has access to a specific resource
 * @param {Array} userGroups - Array of user groups
 * @param {Array} resourceGroups - Array of resource groups
 * @returns {boolean} - True if user has access
 */
export const checkResourceAccess = (userGroups, resourceGroups) => {
    if (!resourceGroups || resourceGroups.length === 0) {
        return true; // No group restrictions
    }

    if (!userGroups || !Array.isArray(userGroups)) {
        return false;
    }

    return userGroups.some((group) => resourceGroups.includes(group));
};

/**
 * Validate project access with permissions and groups
 * @param {Array} userPermissions - Array of user permissions
 * @param {Array} userGroups - Array of user groups
 * @param {Object} project - Project object with allowed_groups
 * @returns {boolean} - True if user has access
 * @throws {Error} - If access is denied
 */
export const validateProjectAccess = (userPermissions, userGroups, project) => {
    // Check if user has project read permission
    if (!hasPermission(userPermissions, 'projects', 'read')) {
        throw new Error('Insufficient permissions: Cannot access projects');
    }

    // Check if user has access to this specific project
    if (!checkResourceAccess(userGroups, project.allowed_groups)) {
        throw new Error('Access denied: Not a member of project groups');
    }

    return true;
};

/**
 * Validate resource access with permissions and ownership
 * @param {Array} userPermissions - Array of user permissions
 * @param {string} resource - Resource name
 * @param {string} action - Action name
 * @param {Object} resourceData - Resource data with owner info
 * @param {string} userId - Current user ID
 * @returns {boolean} - True if user has access
 */
export const validateResourceAccess = (userPermissions, resource, action, resourceData, userId) => {
    // Check basic permission
    if (!hasPermission(userPermissions, resource, action)) {
        throw new Error(`Insufficient permissions: Cannot ${action} ${resource}`);
    }

    // Check ownership for sensitive operations
    const sensitiveActions = ['delete', 'update', 'admin'];
    if (sensitiveActions.includes(action) && resourceData.owner_id && resourceData.owner_id !== userId) {
        // Check if user has admin permission for this resource
        if (!hasPermission(userPermissions, resource, 'admin')) {
            throw new Error(`Access denied: Cannot ${action} ${resource} owned by another user`);
        }
    }

    return true;
};

// ==============================|| USER CONTEXT HELPERS ||============================== //

/**
 * Get user context from localStorage
 * @returns {Object} - User context with permissions, roles, and groups
 */
export const getUserContext = () => {
    try {
        const user_token = localStorage.getItem('user_token');
        const user_permissions = JSON.parse(localStorage.getItem('user_permissions') || '[]');
        const user_roles = JSON.parse(localStorage.getItem('user_roles') || '[]');
        const user_groups = JSON.parse(localStorage.getItem('user_groups') || '[]');
        const user_id = localStorage.getItem('user_id');
        const organization_id = localStorage.getItem('organization_id');

        return {
            user_token,
            user_permissions,
            user_roles,
            user_groups,
            user_id,
            organization_id
        };
    } catch (error) {
        console.error('Error getting user context:', error);
        return {
            user_token: null,
            user_permissions: [],
            user_roles: [],
            user_groups: [],
            user_id: null,
            organization_id: null
        };
    }
};

/**
 * Check if user is authenticated
 * @returns {boolean} - True if user is authenticated
 */
export const isAuthenticated = () => {
    const user_token = localStorage.getItem('user_token');
    return !!user_token;
};

/**
 * Check if user is super admin
 * @param {Array} userRoles - Array of user roles
 * @returns {boolean} - True if user is super admin
 */
export const isSuperAdmin = (userRoles) => {
    return hasRole(userRoles, 'super_admin');
};

/**
 * Check if user is organization admin
 * @param {Array} userRoles - Array of user roles
 * @returns {boolean} - True if user is org admin
 */
export const isOrgAdmin = (userRoles) => {
    return hasRole(userRoles, 'org_admin');
};

/**
 * Check if user is project owner
 * @param {Array} userRoles - Array of user roles
 * @returns {boolean} - True if user is project owner
 */
export const isProjectOwner = (userRoles) => {
    return hasRole(userRoles, 'project_owner');
};

// ==============================|| PERMISSION UTILITIES ||============================== //

/**
 * Get effective permissions for a user (combining role and direct permissions)
 * @param {Array} userRoles - Array of user roles
 * @param {Array} userPermissions - Array of direct user permissions
 * @returns {Array} - Combined effective permissions
 */
export const getEffectivePermissions = (userRoles, userPermissions = []) => {
    const effectivePermissions = new Set(userPermissions);

    // Add permissions from roles
    userRoles.forEach((roleName) => {
        const rolePermissions = getRolePermissions(roleName);
        rolePermissions.forEach((permission) => {
            effectivePermissions.add(permission);
        });
    });

    return Array.from(effectivePermissions);
};

/**
 * Filter resources based on user permissions
 * @param {Array} resources - Array of resources
 * @param {Array} userPermissions - Array of user permissions
 * @param {string} resourceType - Type of resource
 * @param {string} action - Action to check
 * @returns {Array} - Filtered resources
 */
export const filterResourcesByPermission = (resources, userPermissions, resourceType, action) => {
    if (!hasPermission(userPermissions, resourceType, action)) {
        return [];
    }

    return resources.filter((resource) => {
        try {
            return validateResourceAccess(userPermissions, resourceType, action, resource, resource.owner_id);
        } catch (error) {
            console.warn(`Access denied to ${resourceType} ${resource.id}:`, error.message);
            return false;
        }
    });
};

/**
 * Create permission error message
 * @param {string} resource - Resource name
 * @param {string} action - Action name
 * @returns {string} - Formatted error message
 */
export const createPermissionError = (resource, action) => {
    return `Insufficient permissions: Cannot ${action} ${resource}`;
};

// ==============================|| AUDIT HELPERS ||============================== //

/**
 * Create audit log entry for permission check
 * @param {string} userId - User ID
 * @param {string} action - Action performed
 * @param {string} resourceType - Resource type
 * @param {string} resourceId - Resource ID
 * @param {boolean} success - Whether action was successful
 * @param {string} reason - Reason for failure if any
 * @returns {Object} - Audit log entry
 */
export const createAuditEntry = (userId, action, resourceType, resourceId, success, reason = null) => {
    return {
        user_id: userId,
        action,
        resource_type: resourceType,
        resource_id: resourceId,
        success,
        reason,
        timestamp: new Date().toISOString(),
        ip_address: null, // Will be set by backend
        user_agent: navigator.userAgent
    };
};

// ==============================|| EXPORT ||============================== //

export default {
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    hasRole,
    hasAnyRole,
    hasAllRoles,
    checkResourceAccess,
    validateProjectAccess,
    validateResourceAccess,
    getUserContext,
    isAuthenticated,
    isSuperAdmin,
    isOrgAdmin,
    isProjectOwner,
    getEffectivePermissions,
    filterResourcesByPermission,
    createPermissionError,
    createAuditEntry
};
